import LegalLayout from "@/components/LegalLayout";

const H = ({ children }) => (
  <h2 className="font-display text-2xl tracking-tight text-[#171512]">{children}</h2>
);

const A = ({ children }) => (
  <a href={`mailto:${children}`} className="text-[#B9822E] underline-offset-4 hover:underline">
    {children}
  </a>
);

export default function Privacy() {
  return (
    <LegalLayout title="Privacy Policy" updated="Last updated: 17 September 2026">
      <section data-testid="privacy-content" className="space-y-10">
        <p className="rounded-2xl bg-[#FDEFF4] p-6 text-[#171512]/80">
          The short version: we will never sell your data, and we will never give it away to anyone
          for their own use. You share your details to join our waitlist, and that&apos;s the only
          reason we use them.
        </p>

        <div className="space-y-4">
          <H>1. Who we are</H>
          <p>
            This website is run by Nostos, based in Sarjapur, Bengaluru, Karnataka, India.
          </p>
          <p>
            Nostos is a wellness brand from India. Our first product, NIDRA, is a sleep powder you
            mix with water. It isn&apos;t out yet. Right now, you can use this website to join our
            waitlist and our Founding Community.
          </p>
        </div>

        <div className="space-y-4">
          <H>2. What we ask for</H>
          <p>When you sign up, we ask for:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>your name,</li>
            <li>your email address,</li>
            <li>your phone number,</li>
            <li>your gender, only if you want to share it, and</li>
            <li>whether you want to join the Founding Community.</li>
          </ul>
          <p>
            We also note when you signed up, your place on the list, your founding number if you get
            one, and the boxes you ticked.
          </p>
          <p>
            When you visit the website, it may also note basic technical details, like your IP
            address, browser, device and the link that brought you here.
          </p>
          <p>Please don&apos;t send us anything about your health or medicines unless we ask for it.</p>
        </div>

        <div className="space-y-4">
          <H>3. Why we ask for it</H>
          <p>We use your details to:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>add you to the waitlist,</li>
            <li>give you a founding number if you join the Founding Community,</li>
            <li>tell you when NIDRA launches and when early access opens,</li>
            <li>invite Founding Community members to share feedback, help with decisions and come to events,</li>
            <li>send you offers, but only if you said yes to them,</li>
            <li>reply when you contact us, and</li>
            <li>keep the website safe and stop fake sign-ups.</li>
          </ul>
          <p>
            Gender is optional. We ask for it only to understand who is joining our community. It
            never affects your place, your founding number or any benefit.
          </p>
          <p>
            Founding numbers go to the first 200 people who choose to join, in the order they sign
            up. Nothing else decides it.
          </p>
        </div>

        <div className="space-y-4">
          <H>4. We don&apos;t sell or give away your data</H>
          <p>We will never sell your data.</p>
          <p>We will never give it away or share it with other companies for their own marketing.</p>
          <p>
            We only pass your details to the service providers that host this website, store our
            data and send our emails, SMS and WhatsApp messages. Each one gets only what it needs to
            do that job, and can only use your details to do this work for us.
          </p>
          <p>
            Some of these providers may store data outside India. If so, we make sure your data
            stays protected the way Indian law requires.
          </p>
          <p>
            We would only share your details with anyone else if the law requires it, or if Nostos
            is ever bought by or merged with another company. Even then, this policy still protects
            your data.
          </p>
        </div>

        <div className="space-y-4">
          <H>5. Your choices</H>
          <p>You decide what you sign up for. None of the boxes on our form are ticked for you.</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Joining the waitlist means you agree to this policy.</li>
            <li>Joining the Founding Community is your choice.</li>
            <li>Getting offers is your choice.</li>
          </ul>
          <p>
            You can change your mind at any time. Email us at <A>nostosmylkco@gmail.com</A> or use
            the unsubscribe link in any of our emails.
          </p>
          <ul className="list-disc space-y-2 pl-5">
            <li>Stop offers: you stay on the waitlist.</li>
            <li>Leave the Founding Community: you give up your founding number, and no one else gets it.</li>
            <li>Leave the waitlist: we delete your details, unless the law says we must keep something.</li>
          </ul>
        </div>

        <div className="space-y-4">
          <H>6. How we contact you</H>
          <p>We&apos;ll reach you by email, SMS or WhatsApp.</p>
          <p>
            We&apos;ll always send you important updates about your sign-up and the launch. We&apos;ll
            only send offers if you said yes.
          </p>
          <p>We won&apos;t spam you, and we&apos;ll never call you.</p>
        </div>

        <div className="space-y-4">
          <H>7. Cookies</H>
          <p>
            Cookies are small files a website saves on your device. We only use cookies that help
            this website work and show us how it&apos;s used. We don&apos;t use cookies for ads.
          </p>
          <p>
            You can block cookies in your browser settings, but some parts of the site may not work.
          </p>
        </div>

        <div className="space-y-4">
          <H>8. Keeping your data safe</H>
          <p>
            We use secure connections on this website, and only the Nostos team can see your
            details.
          </p>
          <p>
            No system is 100% safe, but we take care to protect your data. If something ever goes
            wrong with your data, we&apos;ll tell you and the authorities, as the law requires.
          </p>
        </div>

        <div className="space-y-4">
          <H>9. How long we keep it</H>
          <p>
            We keep your details until 12 months after NIDRA launches, or until you ask us to delete
            them, whichever comes first. After that, we delete them, unless the law says we must
            keep them longer.
          </p>
        </div>

        <div className="space-y-4">
          <H>10. Age</H>
          <p>
            You need to be 18 or older to sign up. If we find out someone younger has signed up,
            we&apos;ll delete their details.
          </p>
        </div>

        <div className="space-y-4">
          <H>11. Your rights</H>
          <p>You can ask us to:</p>
          <ul className="list-disc space-y-2 pl-5">
            <li>show you what details we have about you,</li>
            <li>fix anything that&apos;s wrong,</li>
            <li>delete your details, or</li>
            <li>stop using them.</li>
          </ul>
          <p>
            As India&apos;s new data protection law comes fully into force, you&apos;ll also be able
            to choose someone to do this for you, and to complain to the Data Protection Board of
            India if we don&apos;t sort out your problem.
          </p>
          <p>
            Just email <A>nostosmylkco@gmail.com</A> from the email you signed up with. We may check
            it&apos;s really you. We&apos;ll reply within 30 days.
          </p>
        </div>

        <div className="space-y-4">
          <H>12. Other websites</H>
          <p>
            If we link to another website, like Instagram, that website&apos;s own privacy policy
            applies.
          </p>
        </div>

        <div className="space-y-4">
          <H>13. Questions or complaints</H>
          <p>Our Grievance Officer handles any question or complaint about your data:</p>
          <p>
            Aarya, Founder
            <br />
            Email: <A>nostosmylkco@gmail.com</A>
            <br />
            Address: Nostos, Sarjapur, Bengaluru, Karnataka, India
          </p>
          <p>We&apos;ll sort out complaints within 30 days.</p>
        </div>

        <div className="space-y-4">
          <H>14. Changes to this policy</H>
          <p>
            If we change this policy, we&apos;ll update the date at the top. If a change affects
            details you&apos;ve already given us, we&apos;ll tell you first.
          </p>
        </div>

        <div className="space-y-4">
          <H>15. Contact us</H>
          <p>
            Nostos
            <br />
            Sarjapur, Bengaluru, Karnataka, India
            <br />
            <A>nostosmylkco@gmail.com</A>
          </p>
        </div>
      </section>
    </LegalLayout>
  );
}
